/*

  e v e n t s  i n t e r f a c e
  Events Interface

  :description:
  A singleton class that utilises the DOM to send/receive events.

*/

//
//  :code:
import { talk } from '../js/talk'

//
//  :statics:
//  Variables and static declarations.
export const EYEKANDY_EVENTS_INTERFACE = 'EyekandyEventsInterface'

//
//  :classes:
export class EyekandyEventsInterface {
  constructor (options) {
    if (typeof options === 'undefined') {
      options = {}
    }
    //
    //  :core:
    //  Core settings and values.
    this.name = 'eyekandy-events-interface'
    this.version = 'v2.0.0'
    this.group = '[EYEKANDY-EVENTS-INTERFACE]'

    //
    //  :runtime:
    this.DOM_ID = 'eyekandy-div'
    this.bus = null

    //
    //  Try to spawn a bus whenever we get created.
    try {
      this.spawn()
    } catch (err) {}

    //
    //  :echo:
    //  Write out into the console that the instance was created.
    talk([this.group, `created a new instance @ '${this.version}'`])
  }

  //
  //  :api:
  //  Exposed API functions for callers.
  //  -   =   -   =   -   =   -   =   -   =   -   =   -   =   -   =   -   =   -   =   -   =   -   =   -   =   -   =   -

  spawn () {
    return this._spawn()
  }

  bindEvent (type, handler, context) {
    //
    //  :step 1:
    //  Correct the context if none was given.
    if (!context) {
      context = this
    }

    //
    //  :step 2:
    //  Add an event listener of this type to our bus.
    this.bus.addEventListener(type, handler.bind(context))

    //
    //  :step 3:
    //  Record in memory that we completed this action.

    //
    //  Return true as we did bind the event to the handler.
    return true
  }

  unbindEvent (type, handler) {
    //
    //  :step 1:
    //  Remove the event listener of this type to our bus.
    this.bus.removeEventListener(type, handler)

    //
    //  :step 2:
    //  Record in memory that we completed this action.

    //
    //  Return true as we did unbind the event from the handler.
    return true
  }

  dispatch (type, detail, destination) {
    if (!detail) {
      detail = null
    }
    //
    //  :step 1:
    //  Get a binding to our event bus and create our event.
    const bus = this.spawn()
    const ekyEvent = new CustomEvent(type, { detail: detail })

    //
    //  :step 2a:
    //  If no destination was given, dispatch the event on the bus.
    if (!destination) {
      bus.dispatchEvent(ekyEvent)
    }

    //
    //  :step 2b:
    //  If set, dispatch on the given destination.
    /* istanbul ignore next */
    if (destination) {
      if (typeof destination['dispatchEvent'] === 'function') {
        destination['dispatchEvent'](ekyEvent)
      }
    }

    //
    //  :step 3:
    //  Record in memory that we completed this action.

    //
    //  Return true as the event was dispatched.
    return true
  }

  _dispatchEvent (event, destination) {
    return this.dispatch(event.type, event.detail, destination)
  }

  //
  //  :internal:
  //  Internal functions, shouldn't need to be called from outside of the class.
  //  -   =   -   =   -   =   -   =   -   =   -   =   -   =   -   =   -   =   -   =   -   =   -   =   -   =   -   =   -

  _selectFromDOM () {
    return document.getElementById(this.DOM_ID)
  }

  _existsInDOM () {
    return this._selectFromDOM() !== null
  }

  _removeFromDOM () {
    if (this._existsInDOM()) {
      this._selectFromDOM().remove()
    }
  }

  _despawn () {
    this._removeFromDOM()
  }

  _spawn () {
    let node = this._selectFromDOM()
    if (!node) {
      node = document.createElement('div')
      node.setAttribute('id', this.DOM_ID)
      document.body.appendChild(node)
    }
    this.bus = node
    return node
  }
}
